import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Sparkles,
  Check,
  Loader2,
  Gamepad2,
  Palette,
  Gauge,
  Repeat,
  Trophy,
  Skull,
  Brush,
  Clock,
  Zap,
  MonitorPlay,
  Layout,
  Music,
  Camera,
  Droplets,
  Map,
  Cpu,
  Quote,
  FileText,
  ChevronDown,
  Layers,
  Blocks,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerClose,
} from '@/components/ui/drawer';
/** GDD2 玩法设计实体（BUG-20260810-068） */
interface GameDesignEntity {
  name: string; role?: string; appearance?: string; behavior?: string; stats?: string;
}

export interface GddSpec {
  summary?: string; gameplayId?: string; gameplayName?: string;
  theme?: string; difficulty?: string; engine?: string;
  coreLoop?: string; winCondition?: string; loseCondition?: string;
  artStyle?: string; colorScheme?: string; particleEffects?: string;
  animationEffects?: string; uiStyle?: string; levelDesign?: string;
  audioStyle?: string; cameraEffect?: string;
  duration?: number; features?: string[];
  /** GDD2 结构化玩法设计段（assetBriefs 随数据传入但不展示——属生图内部契约） */
  gameDesign?: {
    entities?: GameDesignEntity[];
    levelFlow?: string; controls?: string; uiLayout?: string; audioDesign?: string;
    assetBriefs?: Record<string, string>;
  };
}

/** GDD 卡片展示后，用户未手动确认则自动触发生成（秒） */
const GDD_AUTO_CONFIRM_SEC = 10;

interface GddCardProps {
  spec: GddSpec;
  confirmed?: boolean;
  locked?: boolean;
  dimmed?: boolean;
  onConfirm?: () => void;
  loading?: boolean;
  /** 精简模式：只显示核心三行，无确认按钮，点击查看详情 */
  compact?: boolean;
}

type FieldKey = Exclude<keyof GddSpec, 'features'>;

const DIFF_MAP: Record<string, string> = { easy: '简单', medium: '中等', hard: '困难' };

/** 难度徽标配色（低饱和描边 + 浅色底，避免刺眼大色块） */
const DIFF_BADGE: Record<string, string> = {
  easy: 'border-neon-green/30 bg-neon-green/10 text-neon-green',
  medium: 'border-neon-blue/30 bg-neon-blue/10 text-neon-blue',
  hard: 'border-neon-pink/30 bg-neon-pink/10 text-neon-pink',
};

/** 特性 chip 轮换霓虹色 */
const CHIP_COLORS = [
  'border-neon-cyan/25 bg-neon-cyan/10 text-neon-cyan',
  'border-neon-purple/25 bg-neon-purple/10 text-neon-purple',
  'border-neon-pink/25 bg-neon-pink/10 text-neon-pink',
  'border-neon-green/25 bg-neon-green/10 text-neon-green',
  'border-neon-blue/25 bg-neon-blue/10 text-neon-blue',
];

interface FieldDef {
  icon: LucideIcon;
  label: string;
  /** 短字段进两列 grid，长字段整行展示 */
  short?: boolean;
}

const FIELD_DEFS: Partial<Record<FieldKey, FieldDef>> = {
  gameplayName: { icon: Gamepad2, label: '玩法', short: true },
  theme: { icon: Palette, label: '主题', short: true },
  difficulty: { icon: Gauge, label: '难度', short: true },
  duration: { icon: Clock, label: '试玩时长', short: true },
  engine: { icon: Cpu, label: '引擎', short: true },
  coreLoop: { icon: Repeat, label: '核心循环' },
  winCondition: { icon: Trophy, label: '胜利条件' },
  loseCondition: { icon: Skull, label: '失败条件' },
  artStyle: { icon: Brush, label: '美术风格' },
  colorScheme: { icon: Droplets, label: '色彩方案' },
  uiStyle: { icon: Layout, label: 'UI 风格' },
  cameraEffect: { icon: Camera, label: '镜头效果' },
  particleEffects: { icon: Zap, label: '粒子效果' },
  animationEffects: { icon: MonitorPlay, label: '动效' },
  audioStyle: { icon: Music, label: '音频' },
  levelDesign: { icon: Map, label: '关卡设计' },
};

interface SectionDef {
  title: string;
  icon: LucideIcon;
  accent: string; // 竖色条
  iconTint: string; // 图标色
  chip: string; // 图标容器底色
  fields: FieldKey[];
}

const SECTIONS: SectionDef[] = [
  {
    title: '核心玩法',
    icon: Gamepad2,
    accent: 'from-neon-cyan to-neon-blue',
    iconTint: 'text-neon-cyan',
    chip: 'bg-neon-cyan/10',
    fields: ['gameplayName', 'theme', 'difficulty', 'duration', 'engine', 'coreLoop', 'winCondition', 'loseCondition'],
  },
  {
    title: '视觉风格',
    icon: Brush,
    accent: 'from-neon-purple to-neon-pink',
    iconTint: 'text-neon-purple',
    chip: 'bg-neon-purple/10',
    fields: ['artStyle', 'colorScheme', 'uiStyle', 'cameraEffect'],
  },
  {
    title: '动效与音频',
    icon: Zap,
    accent: 'from-neon-pink to-neon-purple',
    iconTint: 'text-neon-pink',
    chip: 'bg-neon-pink/10',
    fields: ['particleEffects', 'animationEffects', 'audioStyle'],
  },
  {
    title: '关卡设计',
    icon: Layers,
    accent: 'from-neon-green to-neon-cyan',
    iconTint: 'text-neon-green',
    chip: 'bg-neon-green/10',
    fields: ['levelDesign'],
  },
];

function renderValue(field: string, raw: unknown): string {
  if (raw === undefined || raw === null) return '';
  if (field === 'difficulty' && typeof raw === 'string') return DIFF_MAP[raw] ?? raw;
  if (field === 'duration') return `${raw} 秒`;
  return String(raw);
}

/** 从 colorScheme 文本中解析 hex 色值（支持 #rgb / #rrggbb，逗号/空格/分号分隔） */
function parseHexColors(raw?: string): string[] | null {
  if (!raw) return null;
  const matches = raw.match(/#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b/g);
  if (!matches) return null;
  return matches.slice(0, 8).map((h) =>
    h.length === 4 ? `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}` : h,
  );
}

function ColorSwatches({ colors }: { colors: string[] }) {
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2">
      {colors.map((hex, i) => (
        <span
          key={`${hex}-${i}`}
          className="flex items-center gap-1.5 rounded-md border border-border/60 bg-background/40 py-1 pl-1.5 pr-2"
        >
          <span
            aria-hidden
            className="h-4 w-4 rounded-full border border-white/15"
            style={{ backgroundColor: hex, boxShadow: `0 0 8px ${hex}55` }}
          />
          <span className="font-mono text-[11px] uppercase text-foreground/85">{hex}</span>
        </span>
      ))}
    </div>
  );
}

function FieldValue({ field, value, className = '' }: { field: FieldKey; value: unknown; className?: string }) {
  if (field === 'colorScheme') {
    const hexes = parseHexColors(typeof value === 'string' ? value : undefined);
    if (hexes) {
      return (
        <div className={className}>
          <ColorSwatches colors={hexes} />
        </div>
      );
    }
  }
  return <p className={`text-[13px] leading-relaxed text-foreground/90 ${className}`}>{renderValue(field, value)}</p>;
}

/* ================= 完整 GDD 弹窗内容 ================= */

export function GddDialogContent({ spec, vertical = false }: { spec: GddSpec; vertical?: boolean }) {
  const diffKey = spec.difficulty && DIFF_MAP[spec.difficulty] ? spec.difficulty : '';
  // GDD2 玩法设计段：有实体表或任一设计文本才展示
  const gd = spec.gameDesign;
  const hasGameDesign = !!gd && Boolean(
    (gd.entities && gd.entities.length > 0) || gd.levelFlow || gd.controls || gd.uiLayout || gd.audioDesign,
  );

  return (
    <div className="stagger-children -mx-1 space-y-4 px-1 pb-1 pt-1">
      {/* ===== Hero：游戏名 + 主题 + 难度徽标 ===== */}
      <div className="relative overflow-hidden rounded-lg border border-neon-cyan/15 bg-gradient-to-br from-neon-cyan/10 via-transparent to-neon-purple/10 p-4">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-neon-purple/15 blur-3xl"
        />
        <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-neon-cyan/80">
          <FileText size={11} /> Game Design Document
        </p>
        <h2 className="neon-text mt-1.5 text-xl font-bold leading-snug">
          {spec.gameplayName || '未命名玩法'}
        </h2>
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          {spec.theme && (
            <span className="flex items-center gap-1.5 rounded-full border border-neon-purple/25 bg-neon-purple/10 px-2.5 py-0.5 text-[11px] text-neon-purple">
              <Palette size={10} /> {spec.theme}
            </span>
          )}
          {diffKey && (
            <span className={`flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] ${DIFF_BADGE[diffKey]}`}>
              <Gauge size={10} /> {DIFF_MAP[diffKey]}
            </span>
          )}
          {typeof spec.duration === 'number' && spec.duration > 0 && (
            <span className="flex items-center gap-1.5 rounded-full border border-border/60 bg-background/30 px-2.5 py-0.5 text-[11px] text-muted-foreground">
              <Clock size={10} /> {spec.duration} 秒
            </span>
          )}
        </div>
      </div>

      {/* ===== Summary 引言块 ===== */}
      {spec.summary && (
        <blockquote className="relative rounded-lg border border-border/60 bg-card/40 py-3 pl-4 pr-3">
          <span aria-hidden className="absolute inset-y-2 left-0 w-[2px] rounded-full bg-gradient-to-b from-neon-cyan to-neon-purple" />
          <Quote size={13} className="mb-1 text-neon-cyan/50" />
          <p className="text-[13px] leading-relaxed text-foreground/85">{spec.summary}</p>
        </blockquote>
      )}

      {/* ===== 分区卡片 ===== */}
      {SECTIONS.map((section) => {
        const present = section.fields.filter(
          (f) => f === 'duration' ? typeof spec.duration === 'number' && spec.duration > 0 : Boolean(spec[f]),
        );
        if (present.length === 0) return null;

        const shortFields = present.filter((f) => FIELD_DEFS[f]?.short);
        const longFields = present.filter((f) => !FIELD_DEFS[f]?.short);

        return (
          <section key={section.title} className="glass-panel p-4">
            {/* 分区小标题：左侧竖色条 + 图标 */}
            <div className="mb-3 flex items-center gap-2">
              <span aria-hidden className={`h-4 w-[3px] rounded-full bg-gradient-to-b ${section.accent}`} />
              <span className={`flex h-5 w-5 items-center justify-center rounded-md ${section.chip}`}>
                <section.icon size={12} className={section.iconTint} />
              </span>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-foreground/90">
                {section.title}
              </h3>
            </div>

            {/* 短字段：两列 grid */}
            {shortFields.length > 0 && (
              <div className={vertical ? 'mb-3 grid grid-cols-1 gap-2.5' : 'mb-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2'}>
                {shortFields.map((f) => {
                  const def = FIELD_DEFS[f]!;
                  const Icon = def.icon;
                  return (
                    <div key={f} className="rounded-md border border-border/40 bg-background/30 px-3 py-2">
                      <div className="mb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                        <Icon size={11} className={section.iconTint} />
                        {def.label}
                      </div>
                      <FieldValue field={f} value={spec[f]} className="font-medium" />
                    </div>
                  );
                })}
              </div>
            )}

            {/* 长字段：整行 */}
            {longFields.map((f, idx) => {
              const def = FIELD_DEFS[f]!;
              const Icon = def.icon;
              return (
                <div key={f} className={idx > 0 ? 'mt-3' : ''}>
                  <div className="mb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                    <Icon size={11} className={section.iconTint} />
                    {def.label}
                  </div>
                  <FieldValue field={f} value={spec[f]} />
                </div>
              );
            })}
          </section>
        );
      })}

      {/* ===== GDD2 玩法设计（实体表/关卡流程/交互/UI/音频明细） ===== */}
      {hasGameDesign && gd && (
        <section className="glass-panel p-4">
          <div className="mb-3 flex items-center gap-2">
            <span aria-hidden className="h-4 w-[3px] rounded-full bg-gradient-to-b from-neon-blue to-neon-cyan" />
            <span className="flex h-5 w-5 items-center justify-center rounded-md bg-neon-blue/10">
              <Blocks size={12} className="text-neon-blue" />
            </span>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-foreground/90">
              玩法设计
            </h3>
          </div>

          {/* 实体表：紧凑卡片行（名称+定位徽标+数值/外观+行为） */}
          {gd.entities && gd.entities.length > 0 && (
            <div className="mb-3 space-y-1.5">
              {gd.entities.map((e, i) => (
                <div key={`${e.name}-${i}`} className="rounded-md border border-border/40 bg-background/30 px-3 py-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[12px] font-semibold text-foreground/90">{e.name}</span>
                    {e.role && (
                      <span className="rounded-full border border-neon-blue/25 bg-neon-blue/10 px-1.5 py-px text-[10px] text-neon-blue">
                        {e.role}
                      </span>
                    )}
                    {e.stats && <span className="text-[10px] text-muted-foreground">{e.stats}</span>}
                  </div>
                  {(e.appearance || e.behavior) && (
                    <p className="mt-0.5 text-[12px] leading-relaxed text-foreground/80">
                      {[e.appearance, e.behavior].filter(Boolean).join('｜')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 设计文本行 */}
          {([
            ['关卡流程', gd.levelFlow],
            ['交互操作', gd.controls],
            ['UI 布局', gd.uiLayout],
            ['音频明细', gd.audioDesign],
          ] as Array<[string, string | undefined]>)
            .filter(([, v]) => v)
            .map(([label, v], idx) => (
              <div key={label} className={idx > 0 ? 'mt-3' : ''}>
                <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
                <p className="text-[13px] leading-relaxed text-foreground/90">{v}</p>
              </div>
            ))}
        </section>
      )}

      {/* ===== 特性标签 ===== */}
      {spec.features && spec.features.length > 0 && (
        <section className="glass-panel p-4">
          <div className="mb-3 flex items-center gap-2">
            <span aria-hidden className="h-4 w-[3px] rounded-full bg-gradient-to-b from-neon-purple to-neon-pink" />
            <span className="flex h-5 w-5 items-center justify-center rounded-md bg-neon-purple/10">
              <Sparkles size={12} className="text-neon-purple" />
            </span>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-foreground/90">特性标签</h3>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {spec.features.map((f, i) => (
              <span
                key={i}
                className={`rounded-full border px-2.5 py-1 text-xs ${CHIP_COLORS[i % CHIP_COLORS.length]}`}
              >
                {f}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* 无障碍标题由 Dialog 消费侧（GddCard）提供：本组件可脱离 Dialog 嵌入任意页面（BUG-20260812-003） */}
    </div>
  );
}

/* ================= 精简卡片 ================= */

export function GddCard({ spec, confirmed, locked, dimmed, onConfirm, loading, compact }: GddCardProps) {
  const [showFullDrawer, setShowFullDrawer] = useState(false);
  const [autoConfirmed, setAutoConfirmed] = useState(false);
  const [countdown, setCountdown] = useState(GDD_AUTO_CONFIRM_SEC);
  const isDone = confirmed || locked;
  const onConfirmRef = useRef(onConfirm);
  onConfirmRef.current = onConfirm;
  const autoConfirmFiredRef = useRef(false);

  const canAutoConfirm = !compact && !isDone && !dimmed && !!onConfirm && !loading;

  useEffect(() => {
    if (!canAutoConfirm) return;

    autoConfirmFiredRef.current = false;
    setAutoConfirmed(false);
    setCountdown(GDD_AUTO_CONFIRM_SEC);
    const endAt = Date.now() + GDD_AUTO_CONFIRM_SEC * 1000;

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining <= 0 && !autoConfirmFiredRef.current) {
        autoConfirmFiredRef.current = true;
        setAutoConfirmed(true);
        onConfirmRef.current?.();
      }
    };

    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [canAutoConfirm]);

  const handleConfirm = () => {
    autoConfirmFiredRef.current = true;
    onConfirmRef.current?.();
  };

  const schemeHexes = useMemo(() => parseHexColors(spec.colorScheme), [spec.colorScheme]);
  const diffKey = spec.difficulty && DIFF_MAP[spec.difficulty] ? spec.difficulty : '';

  const coreRows: Array<{ icon: LucideIcon; label: string; value: string }> = [
    { icon: Gamepad2, label: '玩法', value: spec.gameplayName || '—' },
    { icon: Palette, label: '主题', value: spec.theme || '—' },
    { icon: Gauge, label: '难度', value: diffKey ? DIFF_MAP[diffKey] : spec.difficulty || '—' },
  ];

  return (
    <>
      <div className={dimmed ? 'pointer-events-none opacity-40' : ''}>
        {/* 简洁卡片：固定宽度 + 纵向排布，无渐变/发光装饰 */}
        <div className="w-[520px] overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm">
          {/* 顶部标题条 */}
          <div className="flex items-center gap-2.5 border-b border-border/50 px-5 py-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border/50 bg-muted/60">
              <FileText size={14} className="text-foreground/70" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-foreground">游戏设计文档</h3>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground/70">Game Design Document</p>
            </div>
            {isDone && (
              <span className="flex items-center gap-1.5 rounded-full border border-neon-green/30 bg-neon-green/10 px-2.5 py-1 text-[11px] font-medium text-neon-green">
                <Check size={11} /> 已确认
              </span>
            )}
            {dimmed && (
              <span className="rounded-full border border-border/60 bg-background/50 px-2.5 py-1 text-[10px] text-muted-foreground">
                已过期
              </span>
            )}
            <button
              type="button"
              aria-label="查看完整 GDD"
              onClick={() => setShowFullDrawer(true)}
              className="ml-auto flex items-center gap-1 rounded-lg border border-border/60 px-3 py-1.5 text-xs font-medium text-foreground/80 transition-colors hover:bg-muted"
            >
              查看完整 <ChevronDown size={13} />
            </button>
          </div>

          {/* 核心字段 - 纵向单列，每项独占一行 */}
          <div className="space-y-2 px-5 py-4">
            {coreRows.map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-center gap-3 rounded-lg border border-border/40 bg-muted/30 px-3 py-2.5">
                <Icon size={14} className="shrink-0 text-muted-foreground" />
                <span className="w-12 shrink-0 text-xs text-muted-foreground">{label}</span>
                <span className="flex-1 text-sm text-foreground">{value}</span>
                {label === '主题' && schemeHexes && (
                  <span className="flex shrink-0 items-center gap-1" aria-label="主题配色">
                    {schemeHexes.slice(0, 3).map((hex, i) => (
                      <span
                        key={i}
                        aria-hidden
                        className="h-3 w-3 rounded-full border border-white/20"
                        style={{ backgroundColor: hex }}
                      />
                    ))}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* 底部：确认按钮 / 已确认状态 */}
          {!compact && !dimmed && (
            <div className="border-t border-border/40 px-5 py-3">
              {isDone ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs text-neon-green">
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-neon-green/20">
                      <Check size={11} />
                    </span>
                    {autoConfirmed ? 'AI 已自动确认生成，正在开始制作试玩…' : '已确认，进入生成流程'}
                  </div>
                  {autoConfirmed && (
                    <p className="pl-6 text-[11px] text-muted-foreground">
                      你未在 {GDD_AUTO_CONFIRM_SEC} 秒内手动确认，系统已代为确认并开始生成。
                    </p>
                  )}
                </div>
              ) : onConfirm ? (
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
                  >
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    {loading ? '生成中…' : '确认并生成'}
                  </button>
                  {canAutoConfirm && (
                    <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
                      {countdown > 0
                        ? `${countdown} 秒后未点击将自动确认并生成试玩`
                        : '正在自动确认并生成试玩…'}
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {/* 完整 GDD 详情使用右侧抽屉，保持对话区域紧凑。 */}
      <Drawer open={showFullDrawer} onOpenChange={setShowFullDrawer}>
        <DrawerContent side="right" className="w-full sm:max-w-2xl">
          <DrawerTitle className="sr-only">游戏设计文档：{spec.gameplayName || '完整 GDD'}</DrawerTitle>
          <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-neon-cyan/20 bg-background/95 px-6 py-4 backdrop-blur-sm">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-neon-cyan/30 bg-gradient-to-br from-neon-cyan/15 to-neon-cyan/5">
              <Sparkles size={16} className="text-neon-cyan" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-bold text-foreground">游戏设计文档</h2>
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Full GDD</p>
            </div>
            <DrawerClose className="rounded-lg border border-border/60 bg-background/50 p-2 opacity-70 transition-all hover:border-neon-cyan/40 hover:bg-neon-cyan/10 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan/50">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DrawerClose>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <GddDialogContent spec={spec} vertical />
          </div>
        </DrawerContent>
      </Drawer>

    </>
  );
}
